/** After-action report, the replay viewer ("The Fog Lifts") and its timeline markers. */
import type { OrderKind } from '@friction/engine';
import { type NationRef, type Place, placeLabel, placePhraseOf, shortDesignationEn } from '../names';

const ORDER_WORD: Record<OrderKind, string> = {
  move: 'move',
  attack: 'attack',
  hold: 'hold',
  retreat: 'fall back',
  posture: 'change posture',
};

export const aar = {
  // ---- after-action report
  'aar.head': 'GENERAL STAFF · AFTER-ACTION REPORT',
  'aar.victory': 'VICTORY',
  'aar.defeat': 'DEFEAT',
  'aar.won': (p: { reason: string; days: number }) =>
    `The war is won ${p.reason} after ${p.days} ${p.days === 1 ? 'day' : 'days'}.`,
  'aar.lost': (p: { reason: string; days: number }) =>
    `The war is lost ${p.reason} after ${p.days} ${p.days === 1 ? 'day' : 'days'}.`,
  'aar.reason.conquest': 'by conquest — the last army standing',
  'aar.reason.victory-points': 'on victory points',
  'aar.reason.surrender': 'by capitulation of the enemy',
  'aar.standings': 'Final standings',
  'aar.col.nation': 'Nation',
  'aar.col.vp': 'VP',
  'aar.col.peak': 'Peak',
  'aar.col.hexes': 'Hexes',
  'aar.victor': ' · victor',
  'aar.capitulated': ' · capitulated',
  'aar.record': 'Our record',
  'aar.stat.raised': 'Divisions raised',
  'aar.stat.lost': 'Divisions lost',
  'aar.stat.destroyed': 'Enemy divisions destroyed',
  'aar.stat.cities': 'Cities captured',
  'aar.stat.read': 'Enemy orders read',
  'aar.stat.couriers': 'Couriers lost',
  'aar.stat.peak': 'Peak territory (hexes)',
  'aar.stat.vp': 'Victory points held',
  'aar.stamp.concluded': 'Concluded',
  'aar.stamp.armistice': 'Armistice',
  'aar.stamp.capitulation': 'Capitulation',
  'aar.fog.kicker': 'Newsreel · for the historians',
  'aar.fog.title': 'The Fog Lifts',
  'aar.fog.sub':
    'Replay the war — every army, every courier, as it really was. Then see it as each staff saw it.',
  'aar.save': 'Save record',
  'aar.save.hint': 'Download the war as a .friction-replay file for the film archive',
  'aar.saved': 'Record saved',
  'aar.saveFailed': 'The record could not be saved.',
  'aar.inspect': 'Inspect the map',
  'aar.menu': 'Return to menu',
  'aar.report': 'After-action report',
  'aar.chart.aria': 'Chart',
  'aar.chart.vp': 'Victory points',
  'aar.chart.hexes': 'Territory',
  'aar.chart.us': ' (us)',
  'aar.chart.vpNote': 'Victory points held, by nation. Cities: town 1, city 3, metropolis 6, capital +4.',
  'aar.chart.hexesNote': 'Hexes held, as known to our staff — enemy territory as last observed.',
  'aar.chart.decisive': '{pct} of all VP — decisive',
  'aar.chart.slider': '{metric} of each nation over the war',
  'aar.course': 'The course of the war',

  // ---- replay viewer
  'rp.title': 'The Fog Lifts',
  'rp.back': 'Back to the report',
  'rp.backArchive': 'Back to the archive',
  'rp.omniscient': 'Omniscient',
  'rp.omniscient.hint': 'Everything, everywhere: the fog lifted (V cycles)',
  'rp.nation.hint': (p: { adj: string }) => `As seen by the ${p.adj} General Staff — only what they knew`,
  'rp.truth': 'Truth',
  'rp.truth.hint': 'Pencil in where the enemy really stood (T)',
  'rp.whole': 'The whole truth',
  'rp.whole.sub': 'every army where it stood, every order on the road',
  'rp.seenBy': (p: { adj: string }) => `As seen by the ${p.adj} General Staff`,
  'rp.seenBy.sub': 'only what they knew',
  'rp.seenBy.truth': ' · faint counters: where the enemy really stood',
  'rp.kind.war': 'Outbreak',
  'rp.kind.capture': 'City taken',
  'rp.kind.kessel': 'Kessel',
  'rp.kind.intercept': 'Intercept',
  'rp.kind.capitulation': 'Capitulation',
  'rp.kind.end': 'Armistice',
  'rp.timeline': 'Timeline of the war',
  'rp.backDay': 'Back one day (Shift+←)',
  'rp.back6': 'Back 6 hours (←)',
  'rp.play': 'Play (Space)',
  'rp.pause': 'Pause (Space)',
  'rp.fwd6': 'Forward 6 hours (→)',
  'rp.fwdDay': 'Forward one day (Shift+→)',
  'rp.speed': { one: '{n} hour of war per second (+/−)', other: '{n} hours of war per second (+/−)' },
  'rp.ofDays': 'of {n} days',
  'rp.prev': 'Previous big moment ([)',
  'rp.next': 'Next big moment (])',
  'rp.moment': 'Moment',
  'rp.key.play': 'play',
  'rp.key.hours': '6h',
  'rp.key.day': 'day',
  'rp.key.view': 'view',
  'rp.verified': 'Record verified',
  'rp.diverges': 'Record diverges',
  'rp.verified.hint': 'The re-simulation reached exactly the recorded end of the war.',
  'rp.diverges.hint':
    'This record was made by a different version of the rules; the replay may drift from what happened.',
  'rp.deck': 'Replay controls',
  'rp.torn': 'The film is torn',
  'rp.threading': 'Threading the film…',
  'rp.threading.sub': 'Re-fighting the war from the orders of every staff',

  // ---- observing a war our nation has left (free-for-all)
  'obs.brand': 'From the Heights',
  'obs.fallen.stamp': 'Capitulation',
  'obs.fallen.title': 'Our nation has laid down its arms',
  'obs.fallen.ffa':
    'The war goes on without us. From the heights it can be followed to the end — the whole truth, or as any staff sees it.',
  'obs.fallen.team':
    'Our allies fight on. The staff map stays open and shows what they see — nothing more, so that no word from us can tell them what they cannot know.',
  'obs.observe': 'Observe the war',
  'obs.stay': 'Keep the staff map',
  'obs.leave': 'Leave the war',

  // ---- the film archive (saved replays)
  'arc.kicker': 'Newsreel · Film archive',
  'arc.title': 'Film Archive',
  'arc.footer':
    'Records are saved from the after-action report or from The Fog Lifts, and stay on your machine.',
  'arc.load': 'Load a record…',
  'arc.drop': 'or drop a .friction-replay file here',
  'arc.reading': 'Reading the reel…',
  'arc.empty.title': 'No reel on the projector',
  'arc.empty.text':
    'Save the record of a war — from its after-action report or from The Fog Lifts — then load it here to watch it again.',
  'arc.card.head': 'FILM ARCHIVE · RECORD',
  'arc.saved': 'Saved {date}',
  'arc.length': { one: '{n} day of war', other: '{n} days of war' },
  'arc.nations': 'Belligerents',
  'arc.victors': 'Victory',
  'arc.noVictor': 'nobody',
  'arc.commander': 'commander',
  'arc.computer': 'computer',
  'arc.scenario': 'Scenario',
  'arc.rules.same': 'Recorded under these rules',
  'arc.rules.other':
    'Recorded under other rules — the film may drift from what happened; its seal will tell.',
  'arc.screen': 'Screen the film',
  'arc.another': 'Load another',
  'arc.err.tooLarge': 'That file is too large to be a war record.',
  'arc.err.notReplay': 'That is not a FRICTION war record.',
  'arc.err.newer': 'This record was made by a newer edition of FRICTION. Update the game to screen it.',
  'arc.err.version': 'This record uses a replay format this edition cannot play.',
  'arc.err.corrupt': 'The film is damaged and cannot be read.',
  'arc.err.invalid': 'The record is malformed.',
  'arc.err.read': 'The file could not be read.',

  // ---- timeline markers (worded in every language by the replay worker)
  'mark.war': (p: { theatre: string }) => `War across ${p.theatre}`,
  'mark.capture': (p: { city: string | null; to: string | null; from: string | null; owned: boolean }) =>
    `${p.city ?? 'A city'} taken by the ${p.to ?? 'enemy'}${p.owned ? ` from the ${p.from ?? 'enemy'}` : ''}`,
  'mark.kessel': (p: { n: number; adj: string | null; at: Place }) =>
    `KESSEL — ${p.n} ${p.adj ?? 'enemy'} ${p.n === 1 ? 'division' : 'divisions'} encircled ${placePhraseOf(p.at, 'en')}`,
  'mark.intercept': (p: {
    reader: string | null;
    method: 'captured-courier' | 'decrypted-radio';
    unit: string;
    kind: OrderKind;
    at: Place | null;
  }) =>
    `The ${p.reader ?? 'enemy'} ${p.method === 'captured-courier' ? 'capture a courier' : 'decrypt a signal'}: ${shortDesignationEn(p.unit)} to ${ORDER_WORD[p.kind]}${p.at ? ` at ${placeLabel(p.at, 'en')}` : ''}`,
  'mark.capitulation': (p: { nation: NationRef | null; by: NationRef | null }) =>
    `${p.nation?.name ?? 'the enemy'} capitulates to ${p.by?.name ?? 'the enemy'}`,
  'mark.end': (p: { winners: NationRef[] }) =>
    `Armistice — victory to ${p.winners.map((w) => w.name).join(' & ') || 'nobody'}`,
} as const;
